import React, { useRef } from "react";

import "easymde/dist/easymde.min.css";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { GeoFieldBase } from "@/src/components/form-fields/GeoField";
import {
  RestrictionPeriodFieldBase,
  toTemporalRestrictionFields,
} from "@/src/components/form-fields/RestrictionPeriodField";

import DisplayTranslations from "./DisplayTranslations";
import { CONTENT_MAX_WIDTH_STYLE } from "../../constants/size";
import "../../css/admin/utils/item-status.scss";
import i18n from "../../i18n";
import ItemStatus from "../../utils/ItemStatus";
import Block from "../block/Block";
import SwitchOnOff from "../common/tools/SwitchOnOff";
import Breadcrumbs from "../utils/Breadcrumbs";
import ContainerHeader from "../utils/ContainerHeader";

import type { ChangeEvent, JSX } from "react";

import type { GeoSelection } from "@/src/components/form-fields/GeoField";
import type { RestrictionPeriodChange } from "@/src/components/form-fields/RestrictionPeriodField";

import type { DisplayTranslationsHandle } from "./DisplayTranslations";
import type {
  GeoblockMode,
  GetDisplayResponse,
} from "../../api/SmartlistAPI.types";

const navigationHeaderLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000101745-create-advertising-or-auto-promo-on-your-platform-with-display-formats-promo-banners-",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000101745-cr%C3%A9er-des-formats-publicitaires-ou-auto-promo-display-encarts-banni%C3%A8res-",
};

interface DisplaysFormProps {
  display: GetDisplayResponse;
  setDisplay: React.Dispatch<React.SetStateAction<GetDisplayResponse>>;
  onSubmit: (
    updatedDisplay: GetDisplayResponse,
    onSuccess?: () => void,
  ) => void;
}

export default function DisplaysForm({
  display,
  setDisplay,
  onSubmit,
}: DisplaysFormProps): JSX.Element {
  const { t } = useTranslation(["editor-displays"]);

  const translationsRef = useRef<DisplayTranslationsHandle>(null);

  const onChangeLabel = (
    target: ChangeEvent<HTMLInputElement>["target"],
  ): void => {
    setDisplay((prevDisplay) => ({ ...prevDisplay, label: target.value }));
  };

  const onChangeState = (enabled: boolean): void => {
    setDisplay((prevDisplay) => ({ ...prevDisplay, enabled }));
  };

  const onTargetBlank = (targetBlank: boolean): void => {
    setDisplay((prevDisplay) => ({
      ...prevDisplay,
      target_blank: targetBlank,
    }));
  };

  const onGeoSelection = ({ countries, countryGroups }: GeoSelection): void => {
    setDisplay((prevDisplay) => ({
      ...prevDisplay,
      countries,
      country_groups: countryGroups,
    }));
  };

  const onGeoOptions = (mode: GeoblockMode): void => {
    setDisplay((prevDisplay) => ({ ...prevDisplay, geoblock_mode: mode }));
  };

  const onTempoSelection = (change: RestrictionPeriodChange): void => {
    setDisplay((prevDisplay) => ({
      ...prevDisplay,
      ...toTemporalRestrictionFields(change),
    }));
  };

  const handleSave = async (): Promise<void> => {
    const apiResult = translationsRef.current?.getApiTranslations?.();
    const translations = apiResult?.translations ?? [];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const translations_indexes = apiResult?.translations_indexes ?? {
      language_uuid: {},
      default: { true: [0] },
    };

    const isValidForm = (await translationsRef.current?.isValid()) ?? true;

    if (!isValidForm) {
      toast.error(t("form:validationError", { ns: "form" }));
      return;
    }

    const handleSuccess = (): void => translationsRef.current?.onSaveSuccess();

    onSubmit(
      {
        ...display,
        translations:
          translations as unknown as GetDisplayResponse["translations"],

        translations_indexes,
      },
      handleSuccess,
    );
  };

  const breadcrumbs = [
    {
      name: t("editor-menu:advertising"),
      url: "/displays",
    },
    {
      name: t("sectionTitle"),
      url: "/displays",
    },
    {
      name: t("edit"),
    },
  ];

  return (
    <div
      className="editor-smartlist-form-container editor-has-breadcrumbs mx-auto flex max-w-(--maxWidth) flex-col gap-[0.9375rem]"
      style={CONTENT_MAX_WIDTH_STYLE}
    >
      <Breadcrumbs items={breadcrumbs} />
      <ContainerHeader
        withBackBtn
        defaultLabel={t("unamed")}
        infoLinks={navigationHeaderLinks}
        item={display}
        itemStatus={ItemStatus.getSmartlistStatus(display)}
        onChangeLabel={onChangeLabel}
        onChangeState={onChangeState}
        onSave={handleSave}
      />
      <DisplayTranslations ref={translationsRef} display={display} />
      <Block title={t("options")}>
        <div className="flex items-center gap-[0.9375rem]">
          <SwitchOnOff
            checked={display.target_blank}
            name="target_blank"
            onChange={onTargetBlank}
            size="sm"
          />
          <span>{t("target_blank")}</span>
        </div>
      </Block>
      <Block title={t("restrictions")}>
        <div className="flex max-w-[500px] flex-col gap-8">
          <GeoFieldBase
            geoblockMode={display.geoblock_mode ?? "disabled"}
            onModeChange={onGeoOptions}
            onSelectionChange={onGeoSelection}
            selectedCountries={display.countries ?? []}
            selectedCountryGroups={display.country_groups ?? []}
          />
          <RestrictionPeriodFieldBase
            beginDate={display.begin_date}
            checked={Boolean(display.temporal_restriction)}
            endDate={display.end_date}
            label={t("editor-smartlist:availibility")}
            lang={i18n.language}
            onChange={onTempoSelection}
          />
        </div>
      </Block>
    </div>
  );
}
